/**
 * Alergénios de declaração obrigatória na UE — Reg. (UE) 1169/2011, Anexo II
 * (#325). Os códigos 1–14 seguem a ORDEM OFICIAL do anexo e são o contrato
 * entre o import (sugestão da IA), o painel (confirmação do dono), o
 * cardápio público (#327) e as ground truths do import-bench.
 *
 * Os nomes vivem no banco (ui_texts, chaves `allergen.<código>`, semeadas na
 * migração 0071 a partir DESTE arquivo); aqui ficam como fonte única e
 * fallback embutido. Bases = as 14 línguas oficiais (códigos sem região,
 * #419); cascata exato → base → en.
 */

import { normalizeLocale } from './i18n.js'

export const EU_ALLERGEN_CODES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14] as const
export type AllergenCode = (typeof EU_ALLERGEN_CODES)[number]

/** Identificador estável (inglês) de cada código, para logs e prompts. */
export const EU_ALLERGEN_IDS: Record<AllergenCode, string> = {
  1: 'gluten',
  2: 'crustaceans',
  3: 'eggs',
  4: 'fish',
  5: 'peanuts',
  6: 'soybeans',
  7: 'milk',
  8: 'nuts',
  9: 'celery',
  10: 'mustard',
  11: 'sesame',
  12: 'sulphites',
  13: 'lupin',
  14: 'molluscs',
}

/** Estado de um alergénio de um produto. Só `confirmed` é facto. */
export type AllergenStatus = 'suggested' | 'confirmed'
/** Origem: lido no cardápio impresso, inferido pela IA ou marcado pelo dono. */
export type AllergenSource = 'menu' | 'ai' | 'owner'

export function isAllergenCode(n: unknown): n is AllergenCode {
  return typeof n === 'number' && Number.isInteger(n) && n >= 1 && n <= 14
}

/** Chave de ui_texts do nome de um alergénio. */
export const allergenKey = (code: AllergenCode | number): string => `allergen.${code}`

type Names = readonly [
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
]

/** Nomes curtos por base de idioma, na ordem 1–14. */
export const ALLERGEN_NAMES: Record<string, Names> = {
  pt: [
    'Glúten',
    'Crustáceos',
    'Ovos',
    'Peixes',
    'Amendoim',
    'Soja',
    'Leite',
    'Castanhas e nozes',
    'Aipo',
    'Mostarda',
    'Gergelim',
    'Sulfitos',
    'Tremoço',
    'Moluscos',
  ],
  en: [
    'Gluten',
    'Crustaceans',
    'Eggs',
    'Fish',
    'Peanuts',
    'Soybeans',
    'Milk',
    'Tree nuts',
    'Celery',
    'Mustard',
    'Sesame',
    'Sulphites',
    'Lupin',
    'Molluscs',
  ],
  es: [
    'Gluten',
    'Crustáceos',
    'Huevos',
    'Pescado',
    'Cacahuetes',
    'Soja',
    'Leche',
    'Frutos de cáscara',
    'Apio',
    'Mostaza',
    'Sésamo',
    'Sulfitos',
    'Altramuces',
    'Moluscos',
  ],
  fr: [
    'Gluten',
    'Crustacés',
    'Œufs',
    'Poisson',
    'Arachides',
    'Soja',
    'Lait',
    'Fruits à coque',
    'Céleri',
    'Moutarde',
    'Sésame',
    'Sulfites',
    'Lupin',
    'Mollusques',
  ],
  de: [
    'Gluten',
    'Krebstiere',
    'Eier',
    'Fisch',
    'Erdnüsse',
    'Soja',
    'Milch',
    'Schalenfrüchte',
    'Sellerie',
    'Senf',
    'Sesam',
    'Sulfite',
    'Lupinen',
    'Weichtiere',
  ],
  it: [
    'Glutine',
    'Crostacei',
    'Uova',
    'Pesce',
    'Arachidi',
    'Soia',
    'Latte',
    'Frutta a guscio',
    'Sedano',
    'Senape',
    'Sesamo',
    'Solfiti',
    'Lupini',
    'Molluschi',
  ],
  nl: [
    'Gluten',
    'Schaaldieren',
    'Eieren',
    'Vis',
    "Pinda's",
    'Soja',
    'Melk',
    'Noten',
    'Selderij',
    'Mosterd',
    'Sesamzaad',
    'Sulfieten',
    'Lupine',
    'Weekdieren',
  ],
  zh: [
    '麸质',
    '甲壳类',
    '蛋类',
    '鱼类',
    '花生',
    '大豆',
    '乳制品',
    '坚果',
    '芹菜',
    '芥末',
    '芝麻',
    '亚硫酸盐',
    '羽扇豆',
    '软体动物',
  ],
  ja: [
    'グルテン',
    '甲殻類',
    '卵',
    '魚',
    '落花生',
    '大豆',
    '乳',
    'ナッツ類',
    'セロリ',
    'マスタード',
    'ごま',
    '亜硫酸塩',
    'ルピナス',
    '軟体動物',
  ],
  ko: [
    '글루텐',
    '갑각류',
    '달걀',
    '생선',
    '땅콩',
    '대두',
    '우유',
    '견과류',
    '셀러리',
    '겨자',
    '참깨',
    '아황산염',
    '루핀',
    '연체동물',
  ],
  ru: [
    'Глютен',
    'Ракообразные',
    'Яйца',
    'Рыба',
    'Арахис',
    'Соя',
    'Молоко',
    'Орехи',
    'Сельдерей',
    'Горчица',
    'Кунжут',
    'Сульфиты',
    'Люпин',
    'Моллюски',
  ],
  ar: [
    'الغلوتين',
    'القشريات',
    'البيض',
    'السمك',
    'الفول السوداني',
    'الصويا',
    'الحليب',
    'المكسرات',
    'الكرفس',
    'الخردل',
    'السمسم',
    'الكبريتيتات',
    'الترمس',
    'الرخويات',
  ],
  hi: [
    'ग्लूटेन',
    'क्रस्टेशियन',
    'अंडे',
    'मछली',
    'मूंगफली',
    'सोया',
    'दूध',
    'मेवे',
    'सेलेरी',
    'सरसों',
    'तिल',
    'सल्फ़ाइट',
    'ल्यूपिन',
    'मोलस्क',
  ],
  el: [
    'Γλουτένη',
    'Καρκινοειδή',
    'Αυγά',
    'Ψάρια',
    'Αράπικα φιστίκια',
    'Σόγια',
    'Γάλα',
    'Καρποί με κέλυφος',
    'Σέλινο',
    'Μουστάρδα',
    'Σουσάμι',
    'Θειώδη',
    'Λούπινο',
    'Μαλάκια',
  ],
}

/**
 * Nome no idioma pedido (normalizado para a base; cascata base → en), para o
 * fallback embutido — em produção o texto vem do dicionário (ui_texts), que o
 * dono pode editar.
 */
export function allergenName(code: AllergenCode, locale: string): string {
  const base = normalizeLocale(locale)
  return (ALLERGEN_NAMES[base] ?? ALLERGEN_NAMES.en!)[code - 1]!
}
